import React, { useState } from "react";
import { Card, CardBody, Table, Badge, Button, Input, Spinner } from "reactstrap";

import Breadcrumbs from "../../components/Common/Breadcrumb";

import FbTokenModal from "../../Modal/Facebook/FbTokenModal";
import LeadSyncSettingsModal from "../../Modal/Facebook/LeadSyncSettingsModal";
import FbTokenSetupModal from "../../components/FbLeadgen/FbTokenSetupModal";
import FbTokenDebugModal from "../../Modal/Facebook/FbTokenDebugModal";

import useFbTokens from "../../Hooks/useFbTokens";

const TokenManager = () => {
  const {
    tokens,
    loading,
    refreshing,
    fetchTokens,
    createToken,
    updateToken,
    deleteToken,
    refreshToken,
    refreshAllTokens,
    debugToken,
  } = useFbTokens();

  const [open, setOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [leadSyncModal, setLeadSyncModal] = useState(false);
  const [debugModalOpen, setDebugModalOpen] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [activeRefreshingId, setActiveRefreshingId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");

  const filteredTokens = tokens.filter((item) =>
    item?.name?.toLowerCase()?.includes(search.toLowerCase()) ||
    item?.refId?.toLowerCase()?.includes(search.toLowerCase()) ||
    item?.type?.toLowerCase()?.includes(search.toLowerCase())
  );

  const handleRefreshToken = async (id) => {
    try {
      setActiveRefreshingId(id);
      const res = await refreshToken(id);
      alert(res?.message || "Token refreshed successfully!");
    } catch (err) {
      const errMsg = err?.response?.data?.error || err?.response?.data?.message || err.message || "Refresh failed";
      alert(typeof errMsg === "object" ? (errMsg.message || JSON.stringify(errMsg)) : errMsg);
    } finally {
      setActiveRefreshingId(null);
    }
  };

  const handleRefreshAll = async () => {
    try {
      const res = await refreshAllTokens();
      alert(`Token Refresh Summary:\nRefreshed: ${res?.data?.refreshed || 0}\nFailed: ${res?.data?.failed || 0}`);
    } catch (err) {
      const errMsg = err?.response?.data?.error || err?.response?.data?.message || err.message || "Failed to refresh tokens";
      alert(typeof errMsg === "object" ? (errMsg.message || JSON.stringify(errMsg)) : errMsg);
    }
  };

  const handleInspectToken = async (id) => {
    try {
      setDebugLoading(true);
      setDebugModalOpen(true);
      const res = await debugToken(id);
      setDebugData(res?.data || null);
    } catch (err) {
      const errMsg = err?.response?.data?.error || err?.response?.data?.message || err.message || "Token inspection failed";
      setDebugData({
        error: { message: typeof errMsg === "object" ? (errMsg.message || JSON.stringify(errMsg)) : errMsg },
      });
    } finally {
      setDebugLoading(false);
    }
  };

  return (
    <div className="page-content">
      <Breadcrumbs title="Settings" breadcrumbItem="FB Token Manager" />

      <Card>
        <CardBody>
          <div className="row g-2 mb-3">
            <div className="col-12 col-md">
              <Input
                placeholder="Search token..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="col-12 col-md-auto">
              <div className="d-flex flex-wrap gap-2 justify-content-md-end">
                <Button color="success" onClick={handleRefreshAll} disabled={refreshing}>
                  {refreshing ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    <i className="mdi mdi-refresh me-1" />
                  )}
                  <span>Refresh All Tokens</span>
                </Button>

                <Button color="primary" onClick={() => setLeadSyncModal(true)}>
                  <i className="mdi mdi-sync" />
                  <span className="ms-1 d-none d-md-inline">Lead Sync</span>
                </Button>

                <Button color="primary" onClick={() => setSetupOpen(true)}>
                  <i className="mdi mdi-auto-fix" />
                  <span className="ms-1 d-none d-md-inline">Auto Setup</span>
                </Button>

                <Button
                  color="primary"
                  onClick={() => {
                    setSelected(null);
                    setOpen(true);
                  }}
                >
                  <i className="mdi mdi-plus-circle" />
                  <span className="ms-1 d-none d-md-inline">Manual Token</span>
                </Button>
              </div>
            </div>
          </div>

          <Table bordered hover responsive>
            <thead>
              <tr>
                <th>Name</th>
                <th>Ref ID</th>
                <th>Type</th>
                <th>Status</th>
                <th>Last Refreshed</th>
                <th width="180">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center">
                    <Spinner size="sm" />
                  </td>
                </tr>
              ) : filteredTokens.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center">
                    No Tokens Found
                  </td>
                </tr>
              ) : (
                filteredTokens.map((t) => (
                  <tr key={t._id}>
                    <td>{t.name}</td>
                    <td>{t.refId}</td>
                    <td>
                      <Badge color={t.type === "page" ? "warning" : "primary"}>
                        {t.type}
                      </Badge>
                    </td>
                    <td>
                      <Badge color={t.isActive ? "success" : "danger"}>
                        {t.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    <td>
                      {t.tokenLastRefreshedAt
                        ? new Date(t.tokenLastRefreshedAt).toLocaleString()
                        : new Date(t.updatedAt).toLocaleString()}
                    </td>
                    <td>
                      <div className="d-flex gap-2 align-items-center">
                        {activeRefreshingId === t._id ? (
                          <Spinner size="sm" color="primary" />
                        ) : (
                          <i
                            className="mdi mdi-refresh text-success"
                            style={{ cursor: "pointer", fontSize: 22 }}
                            title="Refresh Token"
                            onClick={() => handleRefreshToken(t._id)}
                          />
                        )}

                        <i
                          className="mdi mdi-shield-search text-info"
                          style={{ cursor: "pointer", fontSize: 22 }}
                          title="Debug / Inspect Token"
                          onClick={() => handleInspectToken(t._id)}
                        />

                        <i
                          className="mdi mdi-pencil text-primary"
                          style={{ cursor: "pointer", fontSize: 22 }}
                          title="Edit Token"
                          onClick={() => {
                            setSelected(t);
                            setOpen(true);
                          }}
                        />

                        <i
                          className="mdi mdi-delete text-danger"
                          style={{ cursor: "pointer", fontSize: 22 }}
                          title="Delete Token"
                          onClick={() => deleteToken(t._id)}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </CardBody>
      </Card>

      <FbTokenModal
        isOpen={open}
        toggle={() => setOpen(false)}
        selected={selected}
        onSave={selected ? updateToken : createToken}
      />

      <FbTokenSetupModal
        isOpen={setupOpen}
        toggle={() => setSetupOpen(false)}
        refresh={fetchTokens}
      />

      <LeadSyncSettingsModal
        isOpen={leadSyncModal}
        toggle={() => setLeadSyncModal(false)}
      />

      <FbTokenDebugModal
        isOpen={debugModalOpen}
        toggle={() => setDebugModalOpen(false)}
        debugData={debugData}
        loading={debugLoading}
      />
    </div>
  );
};

export default TokenManager;